import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Bot, Loader2, Sparkles, Send, RefreshCw, Trash2, CheckCircle2, AlertCircle, Calendar } from "lucide-react";
import AiMemoryWidget from "./AiMemoryWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = {
  surface: "var(--bc-panel, #09090B)",
  highlight: "var(--bc-panel-elev, #141416)",
  border: "var(--bc-border, rgba(255,255,255,0.07))",
  text: "var(--bc-ink, #FFFFFF)",
  muted: "var(--bc-sub, #71717A)",
  violet: "#A78BFA",
  cyan: "#06B6D4",
  green: "#22FFA3",
  amber: "#F59E0B",
  red: "#EF4444",
};

const todayStr = () => {
  // Use local timezone (browser's date), not UTC. Italian users in late
  // evening have UTC date already on the next day → default would be wrong.
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const confidenceColor = (c) => {
  const v = (c || "").toUpperCase();
  if (v === "ALTA") return C.green;
  if (v === "MEDIA") return C.amber;
  return C.muted;
};

function PickRow({ pick, isSelected, onToggle, typeLabel, typeColor }) {
  const isCombo = pick.type === "combo";
  return (
    <label
      data-testid={`ai-pick-row-${pick.fixture_id}-${pick.type || "single"}`}
      style={{
        display: "flex",
        gap: "0.65rem",
        background: isSelected ? `${typeColor}15` : "transparent",
        border: `1px solid ${isSelected ? `${typeColor}66` : C.border}`,
        padding: "0.55rem 0.7rem",
        cursor: "pointer",
        alignItems: "flex-start",
      }}
    >
      <input
        type="checkbox"
        checked={isSelected}
        onChange={onToggle}
        data-testid={`ai-pick-checkbox-${pick.fixture_id}-${pick.type || "single"}`}
        style={{ accentColor: typeColor, marginTop: "0.18rem", width: 13, height: 13, cursor: "pointer" }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.3rem" }}>
          <span style={{
            padding: "0.1rem 0.4rem",
            background: `${typeColor}1a`,
            border: `1px solid ${typeColor}66`,
            color: typeColor,
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.58rem", letterSpacing: "0.12em",
            textTransform: "uppercase", fontWeight: 700,
          }}>
            {typeLabel}
          </span>
          <span style={{
            padding: "0.15rem 0.5rem",
            background: `${C.cyan}15`,
            border: `1px solid ${C.cyan}66`,
            color: C.cyan,
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.75rem", fontWeight: 600,
            letterSpacing: "0.04em",
          }}>
            {pick.bet}
          </span>
          <span style={{ color: C.text, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.75rem", fontWeight: 600 }}>
            {isCombo ? "Joint " : ""}{pick.prob}%
          </span>
          {pick.odds && (
            <span style={{ color: C.amber, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.75rem" }}>
              ×{Number(pick.odds).toFixed(2)}
            </span>
          )}
          <span style={{
            padding: "0.1rem 0.4rem",
            background: "transparent",
            border: `1px solid ${confidenceColor(pick.confidence)}66`,
            color: confidenceColor(pick.confidence),
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.6rem", letterSpacing: "0.1em",
          }}>
            {(pick.confidence || "MEDIA").toUpperCase()}
          </span>
        </div>
        {isCombo && Array.isArray(pick.legs) && pick.legs.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginBottom: "0.35rem" }}>
            {pick.legs.map((leg, i) => (
              <span key={i} style={{
                padding: "0.12rem 0.4rem",
                background: C.surface,
                border: `1px solid ${C.border}`,
                color: C.text,
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.62rem",
                letterSpacing: "0.03em",
              }}>
                <span style={{ color: C.cyan }}>{leg.bet}</span>
                <span style={{ color: C.muted, margin: "0 0.25rem" }}>·</span>
                <span>{leg.prob}%</span>
                {leg.fair_odds && (
                  <>
                    <span style={{ color: C.muted, margin: "0 0.25rem" }}>·</span>
                    <span style={{ color: C.amber }}>×{Number(leg.fair_odds).toFixed(2)}</span>
                  </>
                )}
              </span>
            ))}
          </div>
        )}
        {pick.reasoning && (
          <div style={{
            fontFamily: "'DM Sans',sans-serif",
            fontSize: "0.76rem",
            color: C.muted,
            lineHeight: 1.5,
            fontStyle: "italic",
          }}>
            “{pick.reasoning}”
          </div>
        )}
      </div>
    </label>
  );
}

export default function AiPicksPanel({ token, onToast }) {
  const [date, setDate] = useState(todayStr());
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [generated, setGenerated] = useState(null); // {picks, summary, n_scanned, n_candidates}
  const [peekInfo, setPeekInfo] = useState(null); // {n_total, n_candidates}
  const [selected, setSelected] = useState({}); // {fixture_id+bet: true}
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(null);
  const [populating, setPopulating] = useState(false);
  const [elapsed, setElapsed] = useState(0); // seconds, while generating
  const [resetting, setResetting] = useState(false);
  const [fastMode, setFastMode] = useState(false);
  const [highConfOnly, setHighConfOnly] = useState(false);
  const [highConfTarget, setHighConfTarget] = useState(""); // empty → default 12
  const [progress, setProgress] = useState(null); // {batch, max_batches, n_evaluated, n_alta_found, target}
  const [draftInfo, setDraftInfo] = useState(null); // {exists, picks, n_evaluated}
  const [clearingDraft, setClearingDraft] = useState(false);

  const DEFAULT_HIGH_CONF_TARGET = 12;
  const effectiveTarget = Number(highConfTarget) > 0
    ? Math.min(30, Number(highConfTarget))
    : DEFAULT_HIGH_CONF_TARGET;

  // Live elapsed counter while a job is running
  useEffect(() => {
    if (!generating) { setElapsed(0); return; }
    const t0 = Date.now();
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - t0) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [generating]);

  const fmtElapsed = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec.toString().padStart(2, "0")}s` : `${sec}s`;
  };

  const handleResetJob = async () => {
    if (resetting) return;
    if (!window.confirm(`Resettare il job di generazione per ${date}? Userai questa opzione SOLO se il job è bloccato da molto.`)) return;
    setResetting(true);
    try {
      await axios.post(`${API}/admin/ai-picks/reset-job`, {
        admin_token: token,
        date,
      });
      onToast?.("Job resettato. Ora puoi rilanciare l'analisi.");
      setGenerating(false);
      setGenerateError(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Reset fallito";
      onToast?.(msg, "error");
    } finally {
      setResetting(false);
    }
  };

  const pickKey = (p) => `${p.fixture_id}__${p.type || "single"}__${p.bet}`;

  const loadPublished = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/ai-picks/published`, {
        params: { date, admin_token: token },
      });
      setPublished(res.data?.published ? res.data : null);
    } catch {
      setPublished(null);
    }
  }, [token, date]);

  useEffect(() => {
    loadPublished();
  }, [loadPublished]);

  // Cumulative high-conf draft: picks already found by previous searches
  // for this date (never overwritten, only appended to).
  const loadDraft = useCallback(async () => {
    if (!token || !date) return;
    try {
      const res = await axios.get(`${API}/admin/ai-picks/draft/${date}`, {
        params: { admin_token: token },
      });
      setDraftInfo(res.data?.exists ? res.data : null);
    } catch {
      setDraftInfo(null);
    }
  }, [token, date]);

  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  const handleClearDraft = async () => {
    if (clearingDraft) return;
    if (!window.confirm(`Svuotare i risultati accumulati del ${date}? La prossima ricerca ALTA rianalizzerà tutto il palinsesto da zero.`)) return;
    setClearingDraft(true);
    try {
      await axios.delete(`${API}/admin/ai-picks/draft/${date}`, {
        params: { admin_token: token },
      });
      setDraftInfo(null);
      onToast?.("Risultati accumulati svuotati.");
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Svuotamento fallito", "error");
    } finally {
      setClearingDraft(false);
    }
  };

  // Quick peek when date changes to show n_matches available before clicking Analizza.
  useEffect(() => {
    if (!token || !date) return;
    let cancelled = false;
    (async () => {
      setPeekInfo(null);
      try {
        const res = await axios.get(`${API}/admin/ai-picks/peek`, {
          params: { date, admin_token: token },
        });
        if (!cancelled) setPeekInfo(res.data);
      } catch {
        if (!cancelled) setPeekInfo(null);
      }
    })();
    return () => { cancelled = true; };
  }, [token, date]);

  // Polling helper. Used by both ``handleGenerate`` (right after kicking
  // off a job) and the resume-on-mount effect (when the user reloads the
  // page while a previous job is still running in background).
  const startPolling = useCallback(() => {
    let attempts = 0;
    const maxAttempts = 720; // 720 * 5s = 3600s = 60 min (covers high-conf multi-pass worst case)
    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/admin/ai-picks/generate-status`, {
          params: { date, admin_token: token },
        });
        const st = res.data?.status;
        // Live progress from high-conf multi-batch loop
        if (res.data?.progress) setProgress(res.data.progress);
        if (st === "done") {
          const result = res.data.result;
          setGenerated(result);
          const sel = {};
          (result?.picks || []).forEach((p) => { sel[pickKey(p)] = true; });
          setSelected(sel);
          setProgress(null);
          const nPicks = result?.picks?.length || 0;
          const highConf = result?.high_conf_only;
          if (highConf) {
            const exhausted = result?.search_exhausted;
            const target = result?.target_met;
            const nEval = result?.n_candidates || 0;
            const nBatch = result?.n_batches_run || 0;
            const nNew = result?.n_new_picks;
            const nPrior = result?.n_prior_picks || 0;
            const cumul = nPrior > 0 ? ` (${nNew} nuove + ${nPrior} precedenti)` : "";
            loadDraft();
            if (target) {
              onToast?.(`✓ ${nPicks} pick ALTA totali${cumul} in ${nBatch} batch (${nEval} partite valutate).`);
            } else if (exhausted) {
              onToast?.(`Palinsesto esaurito: ${nPicks} pick ALTA${cumul} su ${nEval} partite valutate (${nBatch} batch).`, "warning");
            } else {
              onToast?.(`Generate ${nPicks} pick ALTA${cumul} in ${nBatch} batch.`);
            }
          } else {
            onToast?.(`Generati ${nPicks} pronostici · ${result?.n_scanned || 0} match analizzati`);
          }
          setGenerating(false);
          return;
        }
        if (st === "error") {
          const msg = res.data.error || "Errore durante la generazione";
          setGenerateError(msg);
          onToast?.(msg, "error");
          setGenerating(false);
          setProgress(null);
          return;
        }
      } catch { /* keep polling on transient errors */ }
      if (attempts >= maxAttempts) {
        setGenerateError("Timeout UI dopo 60 minuti. Il job in background potrebbe essere ancora attivo: ricarica la pagina per riprenderlo, oppure usa 'Reset job' se sembra bloccato.");
        setGenerating(false);
        setProgress(null);
        return;
      }
      setTimeout(poll, 5000);
    };
    setTimeout(poll, 5000);
  }, [date, token, onToast, loadDraft]);

  // Resume polling automatically if a job is already running when the
  // panel mounts or the date changes. Avoids "ghost" state where the
  // user reloads the page mid-generation and thinks nothing is happening.
  useEffect(() => {
    if (!token || !date) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/ai-picks/generate-status`, {
          params: { date, admin_token: token },
        });
        if (cancelled) return;
        if (res.data?.status === "running") {
          onToast?.("Ripresa generazione in corso (avviata in una sessione precedente)…");
          setGenerating(true);
          setGenerateError(null);
          startPolling();
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [token, date, startPolling, onToast]);

  const handlePopulate = async () => {
    if (populating) return;
    if (!window.confirm(`Popolare il calendario per il ${date}? L'operazione gira in background per 1-3 minuti.`)) return;
    setPopulating(true);
    try {
      await axios.post(`${API}/admin/ai-picks/populate`, {
        admin_token: token,
        date,
      });
      onToast?.("Popolamento avviato in background. Aggiorno tra qualche secondo…");
      // Poll peek every 8 seconds for up to 5 minutes
      let attempts = 0;
      const maxAttempts = 40;
      const poll = async () => {
        attempts += 1;
        try {
          const peek = await axios.get(`${API}/admin/ai-picks/peek`, {
            params: { date, admin_token: token },
          });
          setPeekInfo(peek.data);
          if (peek.data?.n_total > 0) {
            onToast?.(`Calendario popolato: ${peek.data.n_total} match disponibili`);
            setPopulating(false);
            return;
          }
        } catch { /* silent */ }
        if (attempts >= maxAttempts) {
          setPopulating(false);
          onToast?.("Popolamento ancora in corso, riprova fra qualche minuto.", "error");
          return;
        }
        setTimeout(poll, 8000);
      };
      setTimeout(poll, 8000);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Popolamento fallito";
      onToast?.(msg, "error");
      setPopulating(false);
    }
  };

  const handleGenerate = async () => {
    if (generating) return;
    setGenerating(true);
    setGenerateError(null);
    setGenerated(null);
    setProgress(null);
    try {
      // Start background job
      await axios.post(`${API}/admin/ai-picks/generate`, {
        admin_token: token,
        date,
        fast_mode: fastMode,
        high_conf_only: highConfOnly,
        high_conf_target: highConfOnly && Number(highConfTarget) > 0 ? effectiveTarget : undefined,
      });
      const msg = highConfOnly
        ? `Claude (Opus 5) sta cercando ${effectiveTarget} partite ALTA con ricerca esaustiva multi-batch${draftInfo?.exists ? " (aggiunte a quelle già trovate)" : ""}…`
        : (fastMode
            ? "Claude (Sonnet 4.5 — modalità veloce) sta analizzando…"
            : "Claude (Opus 5) sta analizzando il palinsesto in background…");
      onToast?.(msg);
      startPolling();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Errore avvio generazione";
      setGenerateError(msg);
      onToast?.(msg, "error");
      setGenerating(false);
    }
  };

  const handlePublish = async () => {
    if (publishing || !generated) return;
    const toPublish = (generated.picks || []).filter((p) => selected[pickKey(p)]);
    if (!toPublish.length) {
      onToast?.("Seleziona almeno un pronostico", "error");
      return;
    }
    setPublishing(true);
    try {
      await axios.post(`${API}/admin/ai-picks/publish`, {
        admin_token: token,
        date,
        summary: generated.summary || "",
        picks: toPublish,
      });
      onToast?.(`Pubblicati ${toPublish.length} pronostici come schedina AI del ${date}`);
      await loadPublished();
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Pubblicazione fallita", "error");
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!published || publishing) return;
    if (!window.confirm(`Rimuovere la schedina AI del ${date}?`)) return;
    setPublishing(true);
    try {
      await axios.delete(`${API}/admin/ai-picks/${date}`, {
        params: { admin_token: token },
      });
      onToast?.("Schedina AI rimossa");
      setPublished(null);
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Rimozione fallita", "error");
    } finally {
      setPublishing(false);
    }
  };

  const toggleSelect = (p) => {
    setSelected((s) => ({ ...s, [pickKey(p)]: !s[pickKey(p)] }));
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div
      data-testid="ai-picks-panel"
      style={{
        background: C.surface,
        border: `1px solid ${C.violet}33`,
        padding: "1.25rem",
        marginBottom: "1.5rem",
        position: "relative",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.65rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        <Bot size={18} style={{ color: C.violet, filter: `drop-shadow(0 0 6px ${C.violet}aa)` }} />
        <span style={{
          fontFamily: "'Barlow Condensed',sans-serif",
          fontWeight: 800,
          fontSize: "1.05rem",
          letterSpacing: "0.15em",
          textTransform: "uppercase",
          color: C.text,
        }}>
          Pronostici AI del Giorno
        </span>
        <span style={{
          padding: "0.18rem 0.5rem",
          background: `${C.violet}1a`,
          border: `1px solid ${C.violet}55`,
          color: C.violet,
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: "0.58rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
        }}>
          Claude Opus 5
        </span>
        {published && (
          <span style={{
            padding: "0.18rem 0.5rem",
            background: `${C.green}1a`,
            border: `1px solid ${C.green}55`,
            color: C.green,
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.6rem",
            letterSpacing: "0.12em",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.3rem",
          }}>
            <CheckCircle2 size={9} /> PUBBLICATO · {published.picks?.length || 0} PICKS
          </span>
        )}
      </div>

      {/* Memoria Claude — toggle widget */}
      <AiMemoryWidget token={token} onToast={onToast} />

      {/* Date + Generate */}
      <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap", marginBottom: "1rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <Calendar size={13} style={{ color: C.muted }} />
          <input
            type="date"
            data-testid="ai-picks-date-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{
              background: C.highlight,
              border: `1px solid ${C.border}`,
              color: C.text,
              padding: "0.45rem 0.6rem",
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.78rem",
              outline: "none",
              colorScheme: "dark",
            }}
          />
          {peekInfo && (
            <span
              data-testid="ai-picks-peek"
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.65rem",
                letterSpacing: "0.06em",
                color: peekInfo.n_candidates > 0 ? C.green : (peekInfo.n_total > 0 ? C.amber : C.red),
                padding: "0.25rem 0.5rem",
                background: C.highlight,
                border: `1px solid ${peekInfo.n_candidates > 0 ? `${C.green}55` : (peekInfo.n_total > 0 ? `${C.amber}55` : `${C.red}55`)}`,
              }}
            >
              {peekInfo.n_total === 0
                ? "Nessun match in DB"
                : `${peekInfo.n_total} match · ${peekInfo.n_candidates} candidati`}
            </span>
          )}
          {peekInfo && peekInfo.small_palinsesto && peekInfo.n_total > 0 && (
            <span
              data-testid="ai-picks-small-flag"
              title="Palinsesto ridotto (<15 partite): il pre-filtro è disattivato, tutte le partite vengono inviate a Claude per l'analisi."
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.6rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "#06FFE4",
                padding: "0.25rem 0.5rem",
                background: "rgba(6,255,228,0.08)",
                border: "1px solid rgba(6,255,228,0.45)",
                textShadow: "0 0 6px rgba(6,255,228,0.6)",
              }}
            >
              ⚡ Tutte analizzate
            </span>
          )}
          {peekInfo && peekInfo.n_total === 0 && (
            <button
              onClick={handlePopulate}
              disabled={populating}
              data-testid="ai-picks-populate-btn"
              style={{
                display: "inline-flex", alignItems: "center", gap: "0.3rem",
                background: `${C.cyan}15`,
                border: `1px solid ${C.cyan}66`,
                color: C.cyan,
                padding: "0.35rem 0.7rem",
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.65rem", letterSpacing: "0.08em",
                textTransform: "uppercase",
                cursor: populating ? "wait" : "pointer",
              }}
            >
              {populating ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
              {populating ? "Popolamento…" : "Popola palinsesto"}
            </button>
          )}
        </div>
        <button
          onClick={handleGenerate}
          disabled={generating}
          data-testid="ai-picks-generate-btn"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.45rem",
            background: generating ? C.highlight : `${C.violet}22`,
            border: `1px solid ${C.violet}88`,
            color: generating ? C.muted : C.violet,
            padding: "0.55rem 1rem",
            fontFamily: "'Barlow Condensed',sans-serif",
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            fontSize: "0.82rem",
            cursor: generating ? "wait" : "pointer",
            boxShadow: generating ? "none" : `0 0 12px ${C.violet}44`,
          }}
        >
          {generating ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
          {generating ? `Claude sta analizzando… ${fmtElapsed(elapsed)}` : "Analizza Palinsesto"}
        </button>
        <label
          data-testid="ai-picks-fast-mode-label"
          title="Sonnet 4.5 invece di Opus: ~30-60s anziché ~2-3min. Qualità ottima ma reasoning leggermente meno profondo."
          style={{
            display: "inline-flex", alignItems: "center", gap: "0.4rem",
            padding: "0.45rem 0.65rem",
            background: fastMode ? `${C.cyan}1a` : "transparent",
            border: `1px solid ${fastMode ? C.cyan : C.border}`,
            color: fastMode ? C.cyan : C.muted,
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.65rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            cursor: generating ? "not-allowed" : "pointer",
            opacity: generating ? 0.5 : 1,
          }}
        >
          <input
            type="checkbox"
            checked={fastMode}
            onChange={(e) => {
              setFastMode(e.target.checked);
              // Radio-mutex: Fast Mode & High-Conf are mutually exclusive
              if (e.target.checked) setHighConfOnly(false);
            }}
            disabled={generating}
            data-testid="ai-picks-fast-mode-checkbox"
            style={{ accentColor: C.cyan, width: 13, height: 13, cursor: "pointer" }}
          />
          ⚡ Modalità Veloce {fastMode && "(~60s)"}
        </label>
        <label
          data-testid="ai-picks-high-conf-label"
          title="Ricerca esaustiva multi-batch: Claude analizza gruppi da 40 partite finché trova il numero di partite ALTA richiesto (default 12, max 30). I risultati si sommano alle ricerche precedenti: le partite già analizzate non vengono ripetute. Tempo: 3-60 min a seconda del target. Solo con Opus 5."
          style={{
            display: "inline-flex", alignItems: "center", gap: "0.4rem",
            padding: "0.45rem 0.65rem",
            background: highConfOnly ? `${C.violet}1a` : "transparent",
            border: `1px solid ${highConfOnly ? C.violet : C.border}`,
            color: highConfOnly ? C.violet : C.muted,
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.65rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            cursor: generating ? "not-allowed" : "pointer",
            opacity: generating ? 0.5 : 1,
          }}
        >
          <input
            type="checkbox"
            checked={highConfOnly}
            onChange={(e) => {
              setHighConfOnly(e.target.checked);
              // Radio-mutex: High-Conf disables Fast Mode automatically
              if (e.target.checked) setFastMode(false);
            }}
            disabled={generating}
            data-testid="ai-picks-high-conf-checkbox"
            style={{ accentColor: C.violet, width: 13, height: 13, cursor: "pointer" }}
          />
          🎯 Solo Alta Affidabilità {highConfOnly && `(min. ${effectiveTarget} partite)`}
        </label>
        {highConfOnly && (
          <label
            data-testid="ai-picks-high-conf-target-label"
            title="Numero di partite ad affidabilità ALTA da cercare attraverso i vari batch. Se lasci vuoto, il default è 12 (max 30)."
            style={{
              display: "inline-flex", alignItems: "center", gap: "0.4rem",
              padding: "0.35rem 0.6rem",
              background: `${C.violet}0d`,
              border: `1px solid ${C.violet}66`,
              color: C.violet,
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.65rem",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            N° partite
            <input
              type="number"
              min={1}
              max={30}
              step={1}
              value={highConfTarget}
              placeholder="12"
              disabled={generating}
              onChange={(e) => setHighConfTarget(e.target.value)}
              data-testid="ai-picks-high-conf-target-input"
              style={{
                width: 58,
                background: C.bg || "transparent",
                border: `1px solid ${C.border}`,
                color: C.violet,
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.72rem",
                padding: "0.2rem 0.35rem",
                textAlign: "center",
              }}
            />
          </label>
        )}
        {generating && elapsed >= 240 && (
          <button
            onClick={handleResetJob}
            disabled={resetting}
            data-testid="ai-picks-reset-job-btn"
            title="Forza il reset del job se sembra bloccato da troppo tempo"
            style={{
              display: "inline-flex", alignItems: "center", gap: "0.3rem",
              background: `${C.amber}15`,
              border: `1px solid ${C.amber}66`,
              color: C.amber,
              padding: "0.45rem 0.75rem",
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.65rem", letterSpacing: "0.08em",
              textTransform: "uppercase",
              cursor: resetting ? "wait" : "pointer",
            }}
          >
            {resetting ? <Loader2 size={11} className="animate-spin" /> : <AlertCircle size={11} />}
            {resetting ? "Reset…" : "Reset job"}
          </button>
        )}
        {published && (
          <button
            onClick={handleUnpublish}
            disabled={publishing}
            data-testid="ai-picks-unpublish-btn"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.35rem",
              background: `${C.red}15`,
              border: `1px solid ${C.red}55`,
              color: C.red,
              padding: "0.45rem 0.85rem",
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.7rem",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              cursor: publishing ? "wait" : "pointer",
            }}
          >
            <Trash2 size={11} /> Rimuovi pubblicato
          </button>
        )}
        <button
          onClick={loadPublished}
          data-testid="ai-picks-refresh-btn"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.3rem",
            background: "transparent",
            border: `1px solid ${C.border}`,
            color: C.muted,
            padding: "0.45rem 0.7rem",
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.7rem",
            letterSpacing: "0.08em",
            cursor: "pointer",
          }}
        >
          <RefreshCw size={11} />
        </button>
      </div>

      {/* Cumulative high-conf draft banner */}
      {draftInfo?.exists && !generating && (
        <div data-testid="ai-picks-draft-banner" style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: "1rem", flexWrap: "wrap",
          background: `${C.violet}0d`, border: `1px solid ${C.violet}44`,
          padding: "0.6rem 0.85rem", marginBottom: "1rem",
          fontFamily: "'JetBrains Mono',monospace", fontSize: "0.72rem",
          color: C.violet,
        }}>
          <span data-testid="ai-picks-draft-info">
            🗂 Ricerche accumulate per {date}: {(draftInfo.picks || []).length} pick ALTA · {draftInfo.n_evaluated} partite già analizzate (non verranno rianalizzate)
          </span>
          <button
            onClick={handleClearDraft}
            disabled={clearingDraft}
            data-testid="ai-picks-clear-draft-btn"
            style={{
              background: "transparent",
              border: `1px solid ${C.violet}66`,
              color: C.violet,
              padding: "0.3rem 0.6rem",
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.63rem", letterSpacing: "0.08em",
              textTransform: "uppercase",
              cursor: clearingDraft ? "wait" : "pointer",
            }}
          >
            {clearingDraft ? "Svuoto…" : "Svuota accumulo"}
          </button>
        </div>
      )}

      {/* Live progress for high-conf multi-batch loop */}
      {generating && progress && (
        <div data-testid="ai-picks-high-conf-progress" style={{
          display: "flex", flexDirection: "column", gap: "0.4rem",
          background: `${C.violet}10`, border: `1px solid ${C.violet}55`,
          padding: "0.65rem 0.85rem", marginBottom: "1rem",
          fontFamily: "'JetBrains Mono',monospace", fontSize: "0.72rem",
          color: C.violet,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
            <span>🎯 Ricerca ALTA — Batch {progress.batch}/{progress.max_batches}</span>
            <span>{progress.n_alta_found}/{progress.target} partite ALTA · {progress.n_evaluated} valutate</span>
          </div>
          <div style={{
            height: 6, background: `${C.violet}22`, position: "relative",
          }}>
            <div style={{
              position: "absolute", top: 0, left: 0, bottom: 0,
              width: `${Math.min(100, (progress.n_alta_found / (progress.target || DEFAULT_HIGH_CONF_TARGET)) * 100)}%`,
              background: C.violet, transition: "width 0.4s ease",
            }} />
          </div>
        </div>
      )}

      {generateError && (
        <div data-testid="ai-picks-error" style={{
          display: "flex", alignItems: "center", gap: "0.5rem",
          background: `${C.red}10`, border: `1px solid ${C.red}55`,
          padding: "0.6rem 0.85rem", marginBottom: "1rem",
          fontFamily: "'JetBrains Mono',monospace", fontSize: "0.78rem",
          color: C.red,
        }}>
          <AlertCircle size={13} /> {generateError}
        </div>
      )}

      {/* Generated picks preview */}
      {generated && (
        <div data-testid="ai-picks-generated" style={{ marginTop: "0.5rem" }}>
          <div style={{
            background: C.highlight,
            border: `1px solid ${C.border}`,
            padding: "0.85rem 1rem",
            marginBottom: "0.85rem",
          }}>
            <div style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.65rem",
              letterSpacing: "0.1em",
              color: C.muted,
              marginBottom: "0.35rem",
              textTransform: "uppercase",
            }}>
              Sommario · {generated.n_candidates} candidati su {generated.n_scanned} analizzati
            </div>
            <div style={{
              fontFamily: "'DM Sans',sans-serif",
              fontSize: "0.85rem",
              color: C.text,
              lineHeight: 1.5,
            }}>
              {generated.summary || "—"}
            </div>
          </div>

          {(generated.picks || []).length === 0 && (
            <div style={{
              padding: "1rem",
              border: `1px dashed ${C.border}`,
              color: C.muted,
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.78rem",
              textAlign: "center",
            }}>
              Claude non ha selezionato nessun pronostico per questa giornata.
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
            {Object.values((generated.picks || []).reduce((acc, p) => {
              const fid = p.fixture_id;
              if (!acc[fid]) acc[fid] = { fixture_id: fid, meta: p, single: null, combo: null };
              if (p.type === "combo") acc[fid].combo = p;
              else acc[fid].single = p;
              // Keep meta from any pick (team/league info)
              acc[fid].meta = acc[fid].meta || p;
              return acc;
            }, {})).map((group) => {
              const meta = group.single || group.combo || group.meta;
              return (
                <div
                  key={group.fixture_id}
                  data-testid={`ai-pick-card-${group.fixture_id}`}
                  style={{
                    background: C.highlight,
                    border: `1px solid ${C.border}`,
                    borderLeft: `3px solid ${C.violet}`,
                    padding: "0.85rem 0.95rem",
                  }}
                >
                  {/* Match header */}
                  <div style={{
                    display: "flex", alignItems: "center", gap: "0.5rem",
                    flexWrap: "wrap", marginBottom: "0.65rem",
                    paddingBottom: "0.55rem", borderBottom: `1px solid ${C.border}`,
                  }}>
                    {meta.home_logo && (
                      <img src={meta.home_logo} alt="" style={{ width: 18, height: 18, objectFit: "contain" }}
                        onError={(e) => (e.target.style.display = "none")} />
                    )}
                    <span style={{
                      fontFamily: "'Barlow Condensed',sans-serif",
                      fontWeight: 700, fontSize: "0.95rem",
                      color: C.text, textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}>
                      {meta.home_team}
                    </span>
                    <span style={{ color: C.muted, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem" }}>vs</span>
                    {meta.away_logo && (
                      <img src={meta.away_logo} alt="" style={{ width: 18, height: 18, objectFit: "contain" }}
                        onError={(e) => (e.target.style.display = "none")} />
                    )}
                    <span style={{
                      fontFamily: "'Barlow Condensed',sans-serif",
                      fontWeight: 700, fontSize: "0.95rem",
                      color: C.text, textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}>
                      {meta.away_team}
                    </span>
                    <span style={{
                      marginLeft: "auto", color: C.muted,
                      fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem",
                      letterSpacing: "0.08em",
                    }}>
                      {meta.league_name}
                    </span>
                  </div>

                  {/* Single row */}
                  {group.single && (
                    <PickRow
                      pick={group.single}
                      isSelected={!!selected[pickKey(group.single)]}
                      onToggle={() => toggleSelect(group.single)}
                      typeLabel="Singolo"
                      typeColor={C.violet}
                    />
                  )}

                  {/* Combo row */}
                  {group.combo && (
                    <div style={{ marginTop: group.single ? "0.55rem" : 0 }}>
                      <PickRow
                        pick={group.combo}
                        isSelected={!!selected[pickKey(group.combo)]}
                        onToggle={() => toggleSelect(group.combo)}
                        typeLabel={group.combo.combo_family ? `Combo · Fam ${group.combo.combo_family}` : "Combo"}
                        typeColor={C.amber}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {(generated.picks || []).length > 0 && (
            <div style={{ display: "flex", gap: "0.75rem", marginTop: "1rem", alignItems: "center", flexWrap: "wrap" }}>
              <button
                onClick={handlePublish}
                disabled={publishing || selectedCount === 0}
                data-testid="ai-picks-publish-btn"
                style={{
                  display: "inline-flex", alignItems: "center", gap: "0.4rem",
                  background: selectedCount > 0 ? `${C.green}1a` : C.highlight,
                  border: `1px solid ${selectedCount > 0 ? `${C.green}77` : C.border}`,
                  color: selectedCount > 0 ? C.green : C.muted,
                  padding: "0.6rem 1.1rem",
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontWeight: 700, letterSpacing: "0.12em",
                  textTransform: "uppercase", fontSize: "0.85rem",
                  cursor: publishing ? "wait" : (selectedCount > 0 ? "pointer" : "not-allowed"),
                  boxShadow: selectedCount > 0 ? `0 0 10px ${C.green}33` : "none",
                }}
              >
                {publishing ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                Pubblica schedina ({selectedCount})
              </button>
              <span style={{ color: C.muted, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem" }}>
                Visibile a tutti gli utenti in &quot;I miei pronostici&quot;
              </span>
            </div>
          )}
        </div>
      )}

      {/* Published preview (when no generation yet) */}
      {!generated && published && (
        <div data-testid="ai-picks-published-preview" style={{
          background: `${C.green}08`,
          border: `1px solid ${C.green}33`,
          padding: "0.9rem 1rem",
        }}>
          <div style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.66rem", letterSpacing: "0.1em",
            color: C.green, marginBottom: "0.45rem",
            textTransform: "uppercase",
          }}>
            Schedina pubblicata · {published.picks.length} picks
          </div>
          <div style={{
            fontFamily: "'DM Sans',sans-serif",
            fontSize: "0.82rem", color: C.text, lineHeight: 1.5,
            marginBottom: "0.6rem",
          }}>
            {published.summary || "—"}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
            {published.picks.map((p, i) => (
              <span key={i} style={{
                padding: "0.18rem 0.5rem",
                background: C.highlight,
                border: `1px solid ${C.border}`,
                color: C.text,
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.7rem",
              }}>
                {p.home_team?.slice(0, 12)}–{p.away_team?.slice(0, 12)} · {p.bet} · {p.prob}%
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
